import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Mail, Phone, ReceiptText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { useAccountOnlySummary } from '@/hooks/useAccountSummary';
import { useOrderPass } from '@/hooks/useOrderPass';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { isOrderingRoute } from '@/lib/ordering-routes';
import { ContactUsMenu } from '@/components/layout/ContactUsMenu';
import {
  SUPPORT_MAILTO_HREF,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';

/**
 * PastDueModal — true blocking overlay that surfaces when the signed-in
 * customer has a past-due balance OR is on credit hold. Locks them out of
 * ordering until they take an action.
 *
 * Behavior:
 *  - Appears on every page when past-due, EXCEPT the user can dismiss it
 *    temporarily ("Continue browsing") to look around. It re-appears on
 *    /order and /cart because that's specifically what we're gating.
 *  - "View invoices" navigates to /invoices and stays dismissed (since the
 *    user is now actively addressing the issue).
 *  - "Chat with us" opens the existing ContactUsMenu inline.
 *  - No X close button. The only way out is to pay (/invoices), contact
 *    support, or click the temporary "Continue browsing" link.
 */
export function PastDueModal() {
  // ⚠ account-ONLY on purpose. This modal BLOCKS order entry, and a chain's
  // master shows the whole group's balance for display. Gating corporate's
  // ordering on 26 stores' collective past-due is a policy change, not a
  // display one.
  const { data: summary } = useAccountOnlySummary();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  // Staff override: superadmins entering an order for a past-due customer
  // (e.g. from Quick order) can suppress this modal for THAT customer for
  // the rest of the session. Keyed by customer id so switching accounts
  // re-arms it. Identity is the authorization — no shared code.
  const { isSuperadmin } = useIsSuperadmin();
  const [overriddenFor, setOverriddenFor] = useState<string | null>(null);
  // A payment clearing that covers the whole past-due amount lets them place
  // ONE order before it posts (useOrderPass). While that order is unused the
  // modal stays away; once used, it explains why ordering is paused again.
  const { data: pass } = useOrderPass(summary?.customerId);

  // Reset the temporary dismiss whenever the user lands on /order or /cart —
  // those are the routes we specifically don't want them on while past-due.
  // ⚠ Exact routes, not startsWith('/order'): that also matched
  // /orders/:order_id, so opening a past order to read it re-raised the
  // modal after "Continue browsing". Changing an order adds items, so
  // /orders/:id/change is gated like /order.
  useEffect(() => {
    if (isOrderingRoute(location.pathname)) {
      setDismissed(false);
    }
  }, [location.pathname]);

  const shouldShow = useMemo(() => {
    if (!summary) return false;
    if (overriddenFor && overriddenFor === summary.customerId) return false;
    if (pass?.eligible) return false;
    if (summary.onCreditHold) return true;
    if (summary.pastDueBalance > 0) return true;
    return false;
  }, [summary, overriddenFor, pass]);

  if (!shouldShow || dismissed) return null;
  if (!summary) return null;

  const days = summary.oldestDaysPastDue ?? 0;
  const onCreditHold = summary.onCreditHold;

  function goPay() {
    setDismissed(true);
    navigate('/invoices');
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="past-due-heading"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
    >
      {/* Backdrop — no click-to-dismiss; intentionally captures interaction
          across the entire screen so the user can't order around it. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
      />

      {/* Card */}
      <div
        className={cn(
          'relative w-full max-w-lg rounded-2xl bg-white shadow-card-deep',
          'border-2 border-red-200 overflow-hidden',
        )}
      >
        {/* Red accent strip */}
        <div className="h-2 bg-red-600" aria-hidden />

        <div className="px-6 pt-6 pb-5 text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-600 text-white flex items-center justify-center shadow-card">
            <AlertCircle size={28} />
          </div>
          <h2
            id="past-due-heading"
            className="font-display text-2xl font-bold text-slate-900 tracking-tight"
          >
            {onCreditHold
              ? 'Your account is on credit hold.'
              : 'You have past-due invoices.'}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed max-w-md mx-auto">
            {pass?.covers_past_due
              ? 'Your payment is still clearing (card: about a day, bank/ACH: 3–5 business days). You have already placed the one order allowed while it clears — ordering resumes once it posts.'
              : onCreditHold
                ? 'New orders are paused until the past-due balance is settled. Pay the full past-due amount and you can place one order while the payment clears.'
                : 'Settle the balance below to keep new orders moving — credit hold kicks in if it ages further.'}
          </p>

          {summary.pastDueBalance > 0 && (
            <div className="mx-auto inline-flex items-baseline gap-2 px-4 py-2 rounded-xl bg-red-50 border border-red-200">
              <span
                className="font-mono text-3xl font-bold text-red-600 tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(summary.pastDueBalance)}
              </span>
              <span className="text-xs font-bold tracking-tight text-red-700">
                past due{days > 0 ? ` · ${days}d` : ''}
              </span>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex flex-col gap-2.5">
          <Button type="button" size="lg" onClick={goPay} className="w-full">
            <ReceiptText size={16} /> View &amp; pay invoices
          </Button>
          <div className="grid grid-cols-2 gap-2.5">
            <a
              href={SUPPORT_MAILTO_HREF}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-brix-border bg-brix-surface text-sm font-semibold text-brix-primary hover:bg-brix-primary-tint/40 transition-colors cursor-pointer"
            >
              <Mail size={14} /> Email us
            </a>
            <a
              href={SUPPORT_TOLL_FREE_HREF}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-brix-border bg-brix-surface text-sm font-semibold text-brix-primary hover:bg-brix-primary-tint/40 transition-colors cursor-pointer"
            >
              <Phone size={14} /> {SUPPORT_PHONE_TOLL_FREE}
            </a>
          </div>
          <div className="flex items-center justify-center pt-1">
            <ContactUsMenu
              variant="inline"
              triggerLabel="Chat with us"
              initialChannel="chat"
            />
          </div>
        </div>

        {/* Escape hatch — temporary dismiss for non-order pages. The modal
            still re-appears on /order or /cart routes. */}
        <div className="px-6 py-3 border-t border-brix-border bg-brix-surface-alt/40 text-center space-y-1.5">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-xs text-brix-muted hover:text-brix-ink underline cursor-pointer"
          >
            Continue browsing (ordering still locked)
          </button>
          {isSuperadmin && (
            <div>
              <button
                type="button"
                onClick={() => setOverriddenFor(summary.customerId)}
                className="text-xs font-bold text-amber-700 hover:text-amber-900 underline cursor-pointer"
              >
                Staff override — continue with order entry
              </button>
              <p className="text-[10px] text-brix-muted mt-0.5">
                Suppresses this notice for this customer this session. A held
                account still needs the override at submit (audit-logged).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
